import React, {useState} from "react";
import Loading from "../../components/dataTable/Loading";
import Banner, {bannerStyle} from "../../components/Banner";
import {logoutSuccess} from "@commons/core.slice";
import InputEx from "../../components/wrapperComponents/InputEx";
import {PasswPolicies} from "@commons/utils/password";
import * as Validation from "@utilities/validationRules";
import {ChangePasswordFormData, useFormChangePassword} from "../../hooks/useFormChangePassword";
import {useAppDispatch} from "@hooks/redux-hooks/hooks";
import {FormProvider, useFormContext} from "react-hook-form";
import {IconCheck} from "@tabler/icons-react";


interface NewPasswordProps {
    askCurrentPassword?: boolean,
    askEmail?: boolean,
    showLoginBtn?: boolean,
    title: () => React.ReactNode,
    onSuccess?: () => void
}

const NewPassword: React.FC<NewPasswordProps> = ({showLoginBtn = true, askCurrentPassword = false, askEmail = true, title, onSuccess}) => {
    const dispatch = useAppDispatch();

    const {
        user,
        onSubmitHandler,
        useFormReturn,
        status,
        statusMsg,
    } = useFormChangePassword('byToken', onSuccess);

    const isLoading = status === 'CHECKING';
    const isSuccess = status === 'SUCCESS';
    const isFailed = status === 'FAILED';

    return (<Loading loading={isLoading} background={false}>
        <div className="mt-2 sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow rounded-lg sm:px-10">
                <FormProvider {...useFormReturn}>
                    <form className="space-y-6 text-sm" onSubmit={onSubmitHandler}>

                        {title()}

                        <ChangePasswordForm askEmail={askEmail} askCurrentPassword={askCurrentPassword} oldEmail={user?.email}/>

                        {isFailed &&
                            <div className="!mt-4">
                                <Banner className="w-full" style={bannerStyle.xdanger} text={statusMsg}/>
                            </div>
                        }

                        {isSuccess &&
                            <div className="!mt-4">
                                <Banner className="w-full" style={bannerStyle.success} text={statusMsg}/>
                            </div>
                        }

                        {!isSuccess &&
                            <div className="!mt-4">
                                <button type="submit"
                                        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600
                                                    hover:bg-blue-700
                                                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                                                    disabled:opacity-50"
                                        disabled={isLoading}>
                                    Invia
                                </button>
                            </div>
                        }
                    </form>
                </FormProvider>
                {showLoginBtn && <div className="grid grid-cols-1 col-end-1 !mt-2 text-sm">
                    <a href="#" className="text-base text-indigo-600 hover:text-indigo-500"
                       onClick={() => dispatch(logoutSuccess())}>
                        Torna alla pagina login
                        <span aria-hidden="true"> &rarr;</span>
                    </a>
                </div>}
            </div>
        </div>
    </Loading>)
}
export default NewPassword;

interface ChangePasswordFormProps {
    //form: UseFormReturn<ChangePasswordFormData, ChangePasswordFormData>,
    askCurrentPassword: boolean,
    askEmail: boolean,
    oldEmail?: string,
}

export const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({
                                                                          //form,
                                                                          askCurrentPassword,
                                                                          askEmail,
                                                                          oldEmail
                                                                      }) => {
    const [passwCheckStatus, setNextPasswCheckStatus] = useState<PasswPolicies>(PasswPolicies.getInstance());
    //const {register, formState: {errors}} = form;
    const {getValues, register, formState: {errors}} = useFormContext<ChangePasswordFormData>();
    // Per validazione password
    const validatePassw = (v: string) => {
        const result = PasswPolicies.validate(v);
        setNextPasswCheckStatus(result);
        return result.checkedCounter === PasswPolicies.numMinRoles;
    }

    const passwordRules = {
        validate: {
            required: (v: string) => (!!v) || 'Campo obbligatorio.',
            validatePassPolicies: (v: string) => validatePassw(v) || 'La password deve essere conforme ai requisiti di complessità',
        }
    }

    const {hasLength, hasCapitalLetter, hasLowercaseLetter, hasNumber, hasPunctuation} = passwCheckStatus;
    const Checked = <IconCheck className="ml-1 text-green-500 h-5 w-5"/>;

    return (<>
            {askCurrentPassword && <>
                <div>
                    <InputEx type="text"
                             label={'Password attuale'}
                             name="currentPassword"
                             register={register}
                             errors={errors}
                             registerOptions={Validation.required}
                             placeholder=""
                    />
                </div>
            </>
            }
            <div>
                <InputEx type="password"
                         label="Nuova password"
                         id="nuovaPassword"
                         name="nuovaPassword"
                         autoCapitalize="none"
                         autoComplete="new-password"
                         maxLength={32}
                         register={register}
                         errors={errors}
                         registerOptions={passwordRules}
                         placeholder=""
                />
            </div>
            <div>
                <p>La tua nuova password deve avere:</p>
                <ul className="list-inside list-disc">
                    <li className="w-full"><span className="inline-flex">Almeno una maiuscola {hasCapitalLetter && Checked}</span></li>
                    <li className="w-full"><span className="inline-flex">Almeno una minuscola {hasLowercaseLetter && Checked}</span></li>
                    <li className="w-full"><span className="inline-flex">Almeno un numero {hasNumber && Checked}</span></li>
                    <li className="w-full"><span
                        className="inline-flex">Almeno un carattere speciale (es. !, $ e ?) {hasPunctuation && Checked}</span>
                    </li>
                    <li className="w-full"><span className="inline-flex">La lunghezza di almeno 8 caratteri {hasLength && Checked}</span></li>
                </ul>
            </div>
            <div>
                <InputEx type="password"
                         label="Ripeti nuova password"
                         id="ripetiPassword"
                         name="ripetiPassword"
                         autoCapitalize="none"
                         autoComplete="repeat-new-password"
                         maxLength={32}
                         register={register}
                         errors={errors}
                         registerOptions={{
                             validate: {
                                 required: v => (!!v) || 'Campo obbligatorio.',
                                 match: v => {
                                     const password = getValues('nuovaPassword');
                                     return password === v || "Le password non coincidono!";
                                 }
                             }
                         }}
                         placeholder=""
                />
            </div>
            {askEmail && <>
                {!oldEmail &&
                    < h3 className="text-blue-500">E' necessario inserire anche un indirizzo email, servirà per permetterti di recuperare la password!</h3>}
                {oldEmail && < h3 className="text-blue-500">Verifica l'indirizzo email fornito, servirà per permetterti di recuperare la password!</h3>}
                <div>
                    <InputEx type="text"
                             label={'Email personale'}
                             name="emailPersonale"
                             register={register}
                             errors={errors}
                             registerOptions={Validation.required}
                             placeholder=""
                    />
                </div>
            </>
            }
        </>
    )
}